/** The review pocket shows the proposed claim, its source, and the current fact. */
import { useState } from "react";
import {
  Button,
  Icons,
  PocketRow,
  PocketSection,
  PocketStatement,
} from "@/components/ui";
import { useT } from "@/i18n";
import { framesMemoryMessages } from "@/i18n/messages/frames-memory";
import { useMemoryStore } from "@/stores/memory-store";

export function PocketSuggestionLevel({ id }: { id: string }) {
  const t = useT(framesMemoryMessages);
  const items = useMemoryStore((state) => state.reviewItems);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const index = items.findIndex((item) => item.id === id);
  const item = items[index];

  if (!item) {
    return (
      <PocketStatement
        size="empty"
        title={t("reviewDone")}
        testId="memory-suggestion-done"
      />
    );
  }

  const decide = async (decision: "accept" | "reject") => {
    setBusy(true);
    setFailed(false);
    try {
      await useMemoryStore.getState().decideSuggestion(item.id, decision);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };
  const step = (delta: 1 | -1) => useMemoryStore.getState().stepReview(delta);

  return (
    <div data-testid="memory-suggestion-level">
      <PocketStatement
        size="panel"
        title={item.title}
        line={<span className="text-warning">{item.reason_label}</span>}
        testId="memory-suggestion-statement"
      />
      {item.current_value ? (
        <PocketSection title={t("currently")}>
          <PocketRow
            testId="memory-suggestion-current"
            title={item.current_value}
          />
        </PocketSection>
      ) : null}
      {item.source_quote ? (
        <PocketSection
          title={item.attributed ? t("fromYourWords") : t("fromConversation")}
        >
          <p
            className="px-2 text-appkit-body italic text-muted [text-wrap:pretty]"
            data-testid="memory-suggestion-quote"
          >
            {item.source_quote}
          </p>
        </PocketSection>
      ) : null}
      <div className="mt-3 flex flex-wrap items-center gap-2 px-2">
        <Button
          size="sm"
          disabled={busy}
          onClick={() => void decide("accept")}
          data-testid="memory-suggestion-accept"
        >
          {t("accept")}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={busy}
          onClick={() => void decide("reject")}
          data-testid="memory-suggestion-reject"
        >
          {t("reject")}
        </Button>
        {items.length > 1 ? (
          <span
            className="ml-auto flex items-center gap-1 text-appkit-caption tabular-nums text-muted"
            data-testid="memory-suggestion-count"
          >
            {t("suggestionPosition", { index: index + 1, total: items.length })}
            <Button
              variant="ghost"
              size="iconSm"
              aria-label={t("previousSuggestion")}
              disabled={busy}
              onClick={() => step(-1)}
              data-testid="memory-suggestion-previous"
            >
              <Icons.ChevronUp className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              variant="ghost"
              size="iconSm"
              aria-label={t("nextSuggestion")}
              disabled={busy}
              onClick={() => step(1)}
              data-testid="memory-suggestion-next"
            >
              <Icons.ChevronDown className="h-4 w-4" aria-hidden="true" />
            </Button>
          </span>
        ) : null}
      </div>
      {failed ? (
        <p
          className="px-2 pt-2 text-appkit-caption text-danger"
          role="alert"
          data-testid="memory-suggestion-error"
        >
          {t("saveChoiceFailed")}
        </p>
      ) : null}
    </div>
  );
}
